import { FormEvent, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight, Mail } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { useToast } from '@/shared/ui/toast-provider';
import { resolveErrorMessage } from '@/shared/lib/errors';
import { postAuthRequestPasswordReset } from '@/entities/auth';
import { normalizeEmail } from '@/shared/utils/normalize-email';

const backgroundImage =
  'https://images.unsplash.com/photo-1497366858526-0766cadbe8fa?q=80&w=2069&auto=format&fit=crop';

export const ForgotPasswordPage = () => {
  const toast = useToast();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<string | null>(null);

  const requestPasswordResetMutation = useMutation({
    mutationFn: postAuthRequestPasswordReset,
    onSuccess: () => {
      setConfirmation(
        "If there's an account associated with this email, check your email for password reset instructions.",
      );
      toast({
        type: 'info',
        title: 'Password reset requested',
        description: 'Check your inbox for further instructions.',
      });
    },
    onError: (mutationError) => {
      const message = resolveErrorMessage(
        mutationError,
        'We could not process your request due to an unexpected error. Please try again.',
      );
      setConfirmation(null);
      // Surfaced inline only (see `error` below) — no error toast double-surfacing.
      setError(message);
    },
  });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!email.trim()) {
      setError('Please enter the email you used to register.');
      setConfirmation(null);
      return;
    }

    setError(null);
    try {
      await requestPasswordResetMutation.mutateAsync({
        email: normalizeEmail(email),
      });
    } catch {
      // Errors handled via onError
    }
  };

  return (
    <main className="relative flex min-h-screen items-center justify-center overflow-hidden bg-slate-950 px-3 py-10 text-slate-50">
      <img
        src={backgroundImage}
        alt="Modern workspace background"
        className="pointer-events-none absolute inset-0 h-full w-full object-cover"
      />
      <div className="absolute inset-0 bg-slate-950/65" aria-hidden />

      <Link
        to="/sign-in"
        search={{ redirect: undefined }}
        className="group absolute left-5 top-5 inline-flex items-center gap-2 rounded-full border border-white/20 bg-slate-950/60 px-4 py-2 text-sm font-medium text-slate-200 shadow-lg shadow-slate-950/40 backdrop-blur transition hover:bg-slate-900/80 hover:text-white"
      >
        <ArrowLeft className="h-4 w-4 transition group-hover:-translate-x-0.5" />
        Back
      </Link>

      <section className="relative z-10 w-full max-w-[420px] space-y-6 rounded-[28px] border border-white/10 bg-slate-900/75 p-8 text-slate-50 shadow-[0_30px_80px_rgba(10,17,33,0.55)] backdrop-blur-2xl">
        <header className="space-y-2 text-center">
          <h1 className="text-2xl font-semibold tracking-tight text-white">
            Find Password
          </h1>
          <p className="text-sm text-slate-200/80">
            Enter your registered email and we'll send you a password reset
            link.
          </p>
        </header>

        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="space-y-1.5">
            <label htmlFor="email" className="sr-only">
              Email address
            </label>
            <div className="relative">
              <Mail className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <Input
                id="email"
                type="email"
                autoComplete="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                inputMode="email"
                placeholder="Email address"
                value={email}
                onChange={(event) => {
                  if (error) setError(null);
                  setConfirmation(null);
                  setEmail(event.target.value);
                }}
                className="h-11 rounded-2xl border-white/20 bg-white/10 pl-10 text-sm text-slate-100 placeholder:text-slate-200/60 focus-visible:ring-blue-500/60 focus-visible:ring-offset-0"
              />
            </div>
          </div>

          {error ? (
            <p className="text-sm font-semibold text-amber-300">{error}</p>
          ) : null}

          {confirmation ? (
            <p className="text-sm text-emerald-200/90">{confirmation}</p>
          ) : null}

          <Button
            type="submit"
            variant="glow"
            size="lg"
            className="w-full gap-2 text-sm h-11"
            disabled={requestPasswordResetMutation.isPending}
          >
            {requestPasswordResetMutation.isPending
              ? 'Sending reset link...'
              : 'Request Password Reset'}
            {!requestPasswordResetMutation.isPending ? (
              <ArrowRight className="h-4 w-4" />
            ) : null}
          </Button>
        </form>

        <footer className="flex items-center justify-center gap-1 text-sm text-slate-200/80 pt-2">
          <span>Remember your password?</span>
          <Link
            to="/sign-in"
            search={{ redirect: undefined }}
            className="font-semibold text-sky-300 transition hover:text-sky-200"
          >
            Sign in
          </Link>
        </footer>
      </section>
    </main>
  );
};
